import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import Link from 'next/link';

import { useAppContext } from '@r1-frontend/entities/Layout/App/AppContext';
import PhoneInfo from '@r1-frontend/entities/Layout/Header/commonComponents/PhoneInfo';
import { ICommonHeaderBaseProps } from '@r1-frontend/entities/Layout/Header/types';
import { useLeadGeneration } from '@r1-frontend/entities/Layout/Header/UnauthHeader/useLeadGeneration';
import { getCurrentCity } from '@r1-frontend/entities/stores/city/selectors';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import WideContainer, { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { EVENTS } from '@r1-frontend/shared/constants/analytics';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import MainMenuItem from '../MainMenuItem';
import PartnersMenuItem from '../PartnersMenuItem';
import ServiceMenuItem from '../ServiceMenuItem';

import * as ST from './styled';

export interface IProps extends ICommonHeaderBaseProps {
    ClientActions?: JSX.Element,
    isAuth: boolean,
    children?: JSX.Element,
}

const CommonDesktopHeader = ({
    items,
    ClientActions,
    isAuth,
    children,
}: IProps): JSX.Element => {
    const city = useSelector(getCurrentCity);
    const { isScrollingUp } = useAppContext();
    // атрибут data-visible для автотестирования
    const isDesktop = useMatchMediaByWidth(wideBreakpoints.laptop, true);
    const { showPhone } = useLeadGeneration();

    const { partners, service, main } = items;

    const onDataLayerSendEventHandler = useCallback((menuType: 'Main' | 'Right', title: string) => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: `${menuType} menu click`,
            action: title,
        });
    }, []);

    return (
        <ST.Wrapper data-test='main-menu-desktop' data-visible={isDesktop ? 'displayed' : 'none'}>
            <ST.TopWrapperOffset isScrollingUp={!!isScrollingUp} data-test={'desktop-top-offset'}>
                <ST.TopWrapper>
                    <WideContainer>
                        <ST.CitySelectionBlock isAuth={isAuth}/>

                        {partners.length
                            ? <ST.PartnersBlock>
                                {partners.map(partner => (
                                    <PartnersMenuItem
                                        key={`desktop-partner-${partner.id}`}
                                        partner={partner}
                                        onDataLayerHandler={onDataLayerSendEventHandler}
                                        data-test='partner-item'
                                    />
                                ))}
                            </ST.PartnersBlock>
                            : null
                        }

                        {service.length
                            ? <ST.ServiceBlock>
                                {service.map(serviceItem => (
                                    <ServiceMenuItem
                                        key={`desktop-service-${serviceItem.id}`}
                                        service={serviceItem}
                                        onDataLayerHandler={onDataLayerSendEventHandler}
                                        data-test='service-item'
                                    />
                                ))}
                            </ST.ServiceBlock>
                            : null
                        }

                        {showPhone ? <PhoneInfo/> : null}
                    </WideContainer>
                </ST.TopWrapper>
            </ST.TopWrapperOffset>

            <ST.MainWrapperOffset data-test={'desktop-main-offset'}>
                <ST.MainWrapper>
                    <ST.Container>
                        <ST.LogoBlock>
                            <Link href='/' passHref>
                                <ST.SMenuLogo href='/' cityData={city}/>
                            </Link>
                        </ST.LogoBlock>

                        {main.length
                            ? <ST.MainBlockInnerContainer>
                                {main.map(mainItem => (
                                    <MainMenuItem
                                        key={`desktop-main-${mainItem.id}`}
                                        mainMenuItem={mainItem}
                                        onDataLayerHandler={onDataLayerSendEventHandler}
                                        data-test='main-item'
                                    />
                                ))}
                            </ST.MainBlockInnerContainer>
                            : null
                        }

                        {ClientActions}

                        {children}
                    </ST.Container>
                </ST.MainWrapper>
            </ST.MainWrapperOffset>
        </ST.Wrapper>);
};

export default CommonDesktopHeader;
